import axios from 'axios';
import { toast } from 'sonner';

import { errorMessage } from './errorMessage';
import { markErrorToastShown } from './toastError';

declare module 'axios' {
  export interface AxiosRequestConfig {
    /** Suppress the global error toast for this request (silent pollers etc). */
    skipErrorToast?: boolean;
  }
}

export const axiosInstance = axios.create({
  headers: {
    'Content-Type': 'application/json',
  },
});

export const setAuthHeader = (token: string): void => {
  axiosInstance.defaults.headers.common.Authorization = `${token}`;
};

export const clearAuthHeader = (): void => {
  delete axiosInstance.defaults.headers.common.Authorization;
};

// When a mobile browser (iOS Safari especially) suspends the tab, every
// in-flight request is killed at the network layer. Their error callbacks
// all fire the moment the user comes back, so a "just resumed" window lets
// the interceptor tell that burst apart from a genuine failure. `online`
// is tracked too because the radio can take a few seconds to re-attach
// after resume, failing even freshly-issued requests.
const RESUME_GRACE_MS = 10_000;
let lastResumeAt = 0;
if (typeof window !== 'undefined') {
  const markResume = () => {
    lastResumeAt = Date.now();
  };
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') markResume();
  });
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) markResume(); // restored from bfcache
  });
  window.addEventListener('online', markResume);
}

// Forward the browser's PostHog web context to our own API so server-side
// captures ('Image created', 'Video Generated', ...) can carry the session
// that triggered them — without this every one of those events has no
// $session_id and landing-page → activation funnels read as zero. The API
// routes bind these headers via bindAnalyticsRequestContext.
//
// Relative URLs only: this client is occasionally pointed at third-party
// hosts, and the session id must never leave our origin.
axiosInstance.interceptors.request.use((config) => {
  if (typeof window === 'undefined') return config;
  const url = config.url || '';
  if (!url.startsWith('/')) return config;
  try {
    // Lazy require so importing this module server-side never pulls
    // posthog-js into an SSR bundle path that might not expect it.
    const posthog = require('posthog-js').default;
    const sessionId = posthog?.get_session_id?.();
    if (sessionId) {
      config.headers = config.headers || {};
      config.headers['X-PostHog-Session-Id'] = sessionId;
      config.headers['X-PostHog-Page'] = window.location.href.slice(0, 512);
    }
  } catch {
    // Analytics must never break a real request.
  }
  return config;
});

// Add a response interceptor
axiosInstance.interceptors.response.use(
  function (response) {
    // Any status code that lie within the range of 2xx cause this function to trigger
    // Do something with response data
    return response;
  },
  function (error) {
    if (
      error.response &&
      error.response.request &&
      error.response.request.responseURL &&
      (error.response.request.responseURL.includes('/api/generate') ||
        error.response.request.responseURL.includes('/api/room-gpt'))
    )
      return Promise.reject(error);

    if (error.config?.skipErrorToast) return Promise.reject(error);

    // Deliberately cancelled requests (component unmount, superseded query)
    // are not errors the user should hear about.
    if (axios.isCancel(error) || error.code === 'ERR_CANCELED')
      return Promise.reject(error);

    // No `error.response` means the request never got an HTTP answer — the
    // connection was cut (tab suspended mid-flight, offline blip, timeout),
    // not a server failure, so "contact support" is the wrong message.
    if (!error.response) {
      const hidden =
        typeof document !== 'undefined' &&
        document.visibilityState === 'hidden';
      const justResumed = Date.now() - lastResumeAt < RESUME_GRACE_MS;
      if (hidden || justResumed) return Promise.reject(error);

      // Fixed toast id: several concurrent requests failing together (e.g.
      // the connection actually dropping) collapse into a single toast.
      toast.error(
        'Connection interrupted. Please check your internet connection and try again.',
        { id: 'network-interrupted' },
      );
      markErrorToastShown(error);
      return Promise.reject(error);
    }

    const fallback =
      'Something went wrong. Please try again later. If the error persists please contact us at support@easy-peasy.ai or via chat.';
    toast.error(errorMessage(error?.response?.data?.error, fallback));
    markErrorToastShown(error);
    // Any status codes that falls outside the range of 2xx cause this function to trigger
    // Do something with response error
    return Promise.reject(error);
  },
);

export default axiosInstance;
